import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tag, Progress, Input, Switch, Button, message, Collapse } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined,
  ClockCircleOutlined, UserOutlined, MailOutlined,
  SafetyCertificateOutlined, HistoryOutlined, CopyOutlined,
  AppstoreOutlined, StopOutlined, BugOutlined,
  ClearOutlined, PlayCircleOutlined, ExclamationCircleOutlined,
  CaretRightOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import {
  verifyEmails, checkHistory, checkDuplicates, checkSpamScore,
  updateCampaign, autoCleanCampaign, clearVerificationResults,
} from '../../../../Slices/bulkOutreachSlice';
import socket from '../../Sales1/socket';

const { Panel } = Collapse;

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  surfaceLight: '#252547',
  border: 'rgba(255,255,255,0.08)',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const STEPS = [
  {
    key: 'completeness',
    title: 'Data Completeness',
    icon: <UserOutlined />,
    desc: 'Check for missing contact names, titles, agency names',
    color: COLORS.blue,
  },
  {
    key: 'optin',
    title: 'Opt-In Check',
    icon: <MailOutlined />,
    desc: 'Verify all contacts have opted in to receive emails',
    color: COLORS.emerald,
  },
  {
    key: 'verification',
    title: 'Email Verification',
    icon: <SafetyCertificateOutlined />,
    desc: 'Verify email addresses (DB cache, MX lookup, ZeroBounce)',
    color: COLORS.violet,
  },
  {
    key: 'invalid',
    title: 'Invalid Email Handling',
    icon: <CloseCircleOutlined />,
    desc: 'Review and fix or remove invalid email addresses',
    color: COLORS.rose,
  },
  {
    key: 'history',
    title: '30-Day History',
    icon: <HistoryOutlined />,
    desc: 'Check if contacts were emailed in the last 30 days',
    color: COLORS.amber,
  },
  {
    key: 'duplicates',
    title: 'Duplicate Detection',
    icon: <CopyOutlined />,
    desc: 'Find same email appearing multiple times in campaign',
    color: '#06b6d4',
  },
  {
    key: 'overlap',
    title: 'Cross-Campaign Overlap',
    icon: <AppstoreOutlined />,
    desc: 'Check contacts that exist in other active campaigns',
    color: '#ec4899',
  },
  {
    key: 'suppression',
    title: 'Suppression Check',
    icon: <StopOutlined />,
    desc: 'Check against bounce and complaint suppression list',
    color: '#f97316',
  },
  {
    key: 'spam',
    title: 'Spam Score',
    icon: <BugOutlined />,
    desc: 'Analyze email content for spam triggers and best practices',
    color: '#a855f7',
  },
];

const STATUS_DISPLAY = {
  pending: {
    icon: <ClockCircleOutlined />,
    color: COLORS.textMuted,
    bg: 'rgba(255,255,255,0.06)',
    label: 'Pending',
  },
  running: {
    icon: <LoadingOutlined spin />,
    color: COLORS.blue,
    bg: COLORS.blueBg,
    label: 'Running...',
  },
  done: {
    icon: <CheckCircleOutlined />,
    color: COLORS.emerald,
    bg: COLORS.emeraldBg,
    label: 'Passed',
  },
  warning: {
    icon: <ExclamationCircleOutlined />,
    color: COLORS.amber,
    bg: COLORS.amberBg,
    label: 'Issues Found',
  },
  error: {
    icon: <CloseCircleOutlined />,
    color: COLORS.rose,
    bg: COLORS.roseBg,
    label: 'Failed',
  },
};

const VerificationPanel = ({ campaignId, recipients = [], steps: campaignSteps = [], subjectLine = '', onComplete, savedPreflightChecks }) => {
  const dispatch = useDispatch();
  const {
    verificationResults, historyResults, duplicateResults, spamScore, loading,
  } = useSelector((s) => s.bulkOutreach);

  // Restore saved statuses if available, otherwise start all pending
  const initialStatuses = (savedPreflightChecks?.stepStatuses?.length === STEPS.length)
    ? savedPreflightChecks.stepStatuses
    : STEPS.map(() => 'pending');

  const [stepStatuses, setStepStatuses] = useState(initialStatuses);
  const [activeStep, setActiveStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [completenessIssues, setCompletenessIssues] = useState([]);
  const [optInIssues, setOptInIssues] = useState([]);
  const [fallbackValues, setFallbackValues] = useState({});
  // Default ON: MX-only verification can't detect dead mailboxes at valid domains (the main
  // bounce source), so deep ZeroBounce verification is the default. Users can toggle it off.
  const [useZeroBounce, setUseZeroBounce] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState([]);
  const [verifyProgress, setVerifyProgress] = useState(null); // { processed, total, valid, invalid, cached, message, phase }
  const hasRestoredRef = useRef(false);

  // Listen for real-time verification progress from backend
  useEffect(() => {
    if (!campaignId) return;
    const handleProgress = (data) => {
      if (data.campaignId !== campaignId) return;
      setVerifyProgress(data);
      if (data.phase === 'complete') {
        // Clear progress after a short delay to show final state
        setTimeout(() => setVerifyProgress(null), 3000);
      }
    };
    socket.on('bulkOutreach:verifyProgress', handleProgress);
    return () => socket.off('bulkOutreach:verifyProgress', handleProgress);
  }, [campaignId]);

  // If savedPreflightChecks arrives after mount (e.g. from async fetch), restore once
  useEffect(() => {
    if (
      savedPreflightChecks?.stepStatuses?.length === STEPS.length &&
      !hasRestoredRef.current
    ) {
      const allPending = stepStatuses.every((s) => s === 'pending');
      if (allPending) {
        setStepStatuses(savedPreflightChecks.stepStatuses);
        hasRestoredRef.current = true;
      }
    }
  }, [savedPreflightChecks]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = useCallback((idx, status) => {
    setStepStatuses((prev) => {
      const next = [...prev];
      next[idx] = status;
      return next;
    });
  }, []);

  const runCompletenessCheck = useCallback(() => {
    updateStatus(0, 'running');
    const issues = [];
    const usedVars = new Set();
    const allText = [subjectLine, ...(campaignSteps || []).map((s) => s.body || '')].join(' ');
    const varRegex = /\{\{(\w+)\}\}/g;
    let match;
    while ((match = varRegex.exec(allText)) !== null) usedVars.add(match[0]);

    const varToField = {
      '{{contactFirstName}}': 'contactName',
      '{{firstName}}': 'contactName',
      '{{contactName}}': 'contactName',
      '{{agencyName}}': 'agencyName',
      '{{contactTitle}}': 'contactTitle',
    };

    for (const v of usedVars) {
      const field = varToField[v];
      if (!field) continue;
      const missing = recipients.filter((r) => !r[field] || String(r[field]).trim() === '');
      if (missing.length > 0) {
        issues.push({
          variable: v,
          field,
          count: missing.length,
          contacts: missing.slice(0, 10),
        });
      }
    }

    setCompletenessIssues(issues);
    updateStatus(0, issues.length > 0 ? 'warning' : 'done');
    return issues.length === 0;
  }, [recipients, campaignSteps, subjectLine, updateStatus]);

  const runOptInCheck = useCallback(() => {
    updateStatus(1, 'running');
    const notOptedIn = recipients.filter((r) => r.optIn === false || r.optIn === 'false');
    setOptInIssues(notOptedIn);
    updateStatus(1, notOptedIn.length > 0 ? 'warning' : 'done');
    return notOptedIn.length === 0;
  }, [recipients, updateStatus]);

  const [verifyError, setVerifyError] = useState(null);
  const [cleanedContacts, setCleanedContacts] = useState([]); // list of removed contacts after auto-clean
  const [isCleaning, setIsCleaning] = useState(false);

  const runEmailVerification = useCallback(async () => {
    if (!campaignId) { updateStatus(2, 'done'); updateStatus(3, 'done'); return true; }
    updateStatus(2, 'running');
    setVerifyError(null);
    setVerifyProgress(null);
    try {
      const result = await dispatch(verifyEmails({ campaignId, useZeroBounce }));
      if (result.error) {
        const errMsg = result.payload || result.error?.message || 'Email verification failed';
        setVerifyError(errMsg);
        updateStatus(2, 'error');
        updateStatus(3, 'error');
        return false;
      }
      const data = result.payload;
      const hasInvalid = (data?.invalidCount || 0) > 0;
      updateStatus(2, hasInvalid ? 'warning' : 'done');
      updateStatus(3, hasInvalid ? 'warning' : 'done');
      return !hasInvalid;
    } catch (err) {
      const errMsg = typeof err === 'string' ? err : (err?.message || 'Email verification failed unexpectedly');
      setVerifyError(errMsg);
      updateStatus(2, 'error');
      updateStatus(3, 'error');
      return false;
    }
  }, [campaignId, useZeroBounce, dispatch, updateStatus]);

  const runHistoryCheck = useCallback(async () => {
    if (!campaignId) { updateStatus(4, 'done'); return true; }
    updateStatus(4, 'running');
    const result = await dispatch(checkHistory(campaignId));
    if (result.error) { updateStatus(4, 'error'); return false; }
    const count = result.payload?.count || 0;
    updateStatus(4, count > 0 ? 'warning' : 'done');
    return count === 0;
  }, [campaignId, dispatch, updateStatus]);

  const runDuplicateChecks = useCallback(async () => {
    if (!campaignId) { updateStatus(5, 'done'); updateStatus(6, 'done'); updateStatus(7, 'done'); return true; }
    updateStatus(5, 'running');
    updateStatus(6, 'running');
    updateStatus(7, 'running');
    const result = await dispatch(checkDuplicates(campaignId));
    if (result.error) {
      updateStatus(5, 'error');
      updateStatus(6, 'error');
      updateStatus(7, 'error');
      return false;
    }
    const d = result.payload || {};
    updateStatus(5, (d.internalDuplicateCount || 0) > 0 ? 'warning' : 'done');
    updateStatus(6, (d.crossCampaignOverlapCount || 0) > 0 ? 'warning' : 'done');
    updateStatus(7, (d.inCooldownCount || 0) > 0 ? 'warning' : 'done');
    return (d.internalDuplicateCount || 0) === 0 && (d.inCooldownCount || 0) === 0;
  }, [campaignId, dispatch, updateStatus]);

  const runSpamCheck = useCallback(async () => {
    updateStatus(8, 'running');
    const body = campaignSteps?.[0]?.body || '';
    const result = await dispatch(checkSpamScore({ subject: subjectLine, body }));
    if (result.error) { updateStatus(8, 'error'); return false; }
    const score = result.payload?.score || 0;
    updateStatus(8, score > 40 ? 'warning' : 'done');
    return score <= 40;
  }, [campaignSteps, subjectLine, dispatch, updateStatus]);

  // Persist preflight check statuses to backend so reopening a draft shows completed checks
  const savePreflightStatuses = useCallback((statuses) => {
    if (!campaignId) return;
    const issueCount =
      completenessIssues.length +
      optInIssues.length +
      (verificationResults?.invalidCount || 0) +
      (historyResults?.count || 0) +
      (duplicateResults?.internalDuplicateCount || 0) +
      (duplicateResults?.inCooldownCount || 0);
    dispatch(updateCampaign({
      id: campaignId,
      preflightChecks: {
        stepStatuses: statuses,
        completedAt: new Date().toISOString(),
        issueCount,
      },
    }));
  }, [campaignId, dispatch, completenessIssues, optInIssues, verificationResults, historyResults, duplicateResults]);

  // Auto-clean: remove all invalid emails from the campaign
  const handleAutoClean = useCallback(async () => {
    if (!campaignId) {
      message.warning('Save the campaign as draft first before auto-cleaning');
      return;
    }
    setIsCleaning(true);
    try {
      const result = await dispatch(autoCleanCampaign(campaignId)).unwrap();
      if (result.removedCount > 0) {
        setCleanedContacts(result.removed || []);
        // Update the verification/invalid steps to reflect cleaned state
        updateStatus(2, 'done');
        updateStatus(3, 'done');
        // Clear stale verification results from Redux so badges update correctly
        dispatch(clearVerificationResults());
        // Save updated statuses to backend immediately
        setTimeout(() => {
          setStepStatuses((current) => {
            savePreflightStatuses(current);
            return current;
          });
        }, 100);
        message.success(`Removed ${result.removedCount} invalid contacts. ${result.remainingCount} remaining.`);
      } else {
        message.info('No invalid contacts to remove');
      }
    } catch (err) {
      message.error(typeof err === 'string' ? err : 'Auto-clean failed');
    } finally {
      setIsCleaning(false);
    }
  }, [campaignId, dispatch, updateStatus]);

  const runAllChecks = useCallback(async () => {
    setIsRunning(true);
    setActiveStep(0);

    runCompletenessCheck();
    setActiveStep(1);

    runOptInCheck();
    setActiveStep(2);

    await runEmailVerification();
    setActiveStep(4);

    await runHistoryCheck();
    setActiveStep(5);

    await runDuplicateChecks();
    setActiveStep(8);

    await runSpamCheck();

    setIsRunning(false);
    message.success('All verification checks complete');

    // Save the final statuses to backend after a tick so state has settled
    setTimeout(() => {
      setStepStatuses((finalStatuses) => {
        savePreflightStatuses(finalStatuses);
        return finalStatuses;
      });
    }, 100);
  }, [runCompletenessCheck, runOptInCheck, runEmailVerification, runHistoryCheck, runDuplicateChecks, runSpamCheck, savePreflightStatuses]);

  const allPassed = stepStatuses.every((s) => s === 'done');
  const hasWarnings = stepStatuses.some((s) => s === 'warning');
  const hasErrors = stepStatuses.some((s) => s === 'error');
  const completedCount = stepStatuses.filter((s) => s === 'done' || s === 'warning').length;
  const overallProgress = Math.round((completedCount / STEPS.length) * 100);
  // Only count issues from Redux if checks have actually been run in this session;
  // otherwise use the saved issue count from the backend (or 0 if never verified)
  const checksWereRun = stepStatuses.some((s) => s !== 'pending');
  const totalIssues = checksWereRun
    ? (completenessIssues.length +
       optInIssues.length +
       (verificationResults?.invalidCount || 0) +
       (historyResults?.count || 0) +
       (duplicateResults?.internalDuplicateCount || 0) +
       (duplicateResults?.inCooldownCount || 0))
    : (savedPreflightChecks?.issueCount || 0);

  const getStepIssueDetail = (stepKey, status) => {
    if (status !== 'warning' && status !== 'error') return null;

    const detailStyle = {
      marginTop: 10,
      padding: '10px 14px',
      borderRadius: 8,
      background: status === 'error' ? COLORS.roseBg : COLORS.amberBg,
      fontSize: 12,
      color: status === 'error' ? COLORS.rose : COLORS.amber,
    };

    switch (stepKey) {
      case 'completeness':
        if (completenessIssues.length === 0) return null;
        return (
          <div style={detailStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: COLORS.text }}>
              Missing data -- provide fallback values:
            </div>
            {completenessIssues.map((issue) => (
              <div key={issue.variable} style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
              }}>
                <Tag style={{
                  color: COLORS.amber, background: COLORS.amberBg, border: 'none',
                  borderRadius: 5, fontSize: 11, fontWeight: 600,
                }}>
                  {issue.variable}
                </Tag>
                <span style={{ fontSize: 11, color: COLORS.textSecondary }}>
                  {issue.count} contacts missing
                </span>
                <Input
                  size="small"
                  placeholder={`Fallback for ${issue.variable}`}
                  value={fallbackValues[issue.variable] || ''}
                  onChange={(e) => setFallbackValues({ ...fallbackValues, [issue.variable]: e.target.value })}
                  style={{ width: 200, borderRadius: 6 }}
                />
              </div>
            ))}
          </div>
        );

      case 'optin':
        if (optInIssues.length === 0) return null;
        return (
          <div style={detailStyle}>
            <strong>{optInIssues.length}</strong> contacts have not opted in to receive emails.
            They will be skipped if consent is required.
          </div>
        );

      case 'verification':
        // Show error message if verification failed
        if (status === 'error' && verifyError) {
          return (
            <div style={detailStyle}>
              <strong>Verification failed:</strong> {verifyError}
              <div style={{ marginTop: 4, fontSize: 11, color: COLORS.textSecondary }}>
                Check your network connection and try again. If using ZeroBounce, verify API credits are available.
              </div>
            </div>
          );
        }
        if (!verificationResults || !verificationResults.invalidCount) return null;
        return (
          <div style={detailStyle}>
            <div style={{ marginBottom: 4 }}>
              <strong>{verificationResults.invalidCount}</strong> invalid emails found
              {verificationResults.validCount > 0 && (
                <span style={{ color: COLORS.emerald, marginLeft: 8 }}>
                  ({verificationResults.validCount} valid)
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
              Invalid emails are automatically excluded from sending.
            </div>
          </div>
        );

      case 'invalid':
        if (status === 'error' && verifyError) {
          return (
            <div style={detailStyle}>
              <strong>Verification failed:</strong> {verifyError}
            </div>
          );
        }
        if (!verificationResults?.results?.length && !verificationResults?.invalidCount) return null;
        {
          const invalidList = (verificationResults?.results || []).filter(r => !r.isValid);
          if (invalidList.length === 0) return null;
          return (
            <div style={detailStyle}>
              <div style={{ marginBottom: 8, color: COLORS.text, fontWeight: 600 }}>
                {invalidList.length} emails excluded from sending:
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', borderRadius: 6, background: 'rgba(0,0,0,0.15)', padding: 8 }}>
                {invalidList.slice(0, 50).map((r, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '4px 8px', borderBottom: idx < invalidList.length - 1 ? `1px solid ${COLORS.glassBorder}` : 'none',
                    fontSize: 11,
                  }}>
                    <span style={{ color: COLORS.rose, fontWeight: 500 }}>{r.email}</span>
                    <Tag style={{
                      color: COLORS.rose, background: COLORS.roseBg,
                      border: 'none', borderRadius: 4, fontSize: 10, margin: 0,
                    }}>
                      {r.reason || r.code || 'Invalid'}
                    </Tag>
                  </div>
                ))}
                {invalidList.length > 50 && (
                  <div style={{ padding: '6px 8px', fontSize: 11, color: COLORS.textMuted, textAlign: 'center' }}>
                    ...and {invalidList.length - 50} more
                  </div>
                )}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: COLORS.textSecondary }}>
                These contacts are marked as <strong>verification_failed</strong> and will be skipped during sending.
                Use <strong>Auto-Clean</strong> to permanently remove them.
              </div>
            </div>
          );
        }

      case 'history':
        if (!historyResults || !historyResults.count) return null;
        return (
          <div style={detailStyle}>
            <strong>{historyResults.count}</strong> contacts were emailed in the last 30 days.
            Consider applying cooldown or excluding them.
          </div>
        );

      case 'duplicates':
        if (!duplicateResults) return null;
        return (
          <div style={detailStyle}>
            {(duplicateResults.internalDuplicateCount || 0) > 0 && (
              <div><strong>{duplicateResults.internalDuplicateCount}</strong> duplicate emails in this campaign.</div>
            )}
          </div>
        );

      case 'overlap':
        if (!duplicateResults || !duplicateResults.crossCampaignOverlapCount) return null;
        return (
          <div style={detailStyle}>
            <strong>{duplicateResults.crossCampaignOverlapCount}</strong> contacts appear in other active campaigns.
          </div>
        );

      case 'suppression':
        if (!duplicateResults || !duplicateResults.inCooldownCount) return null;
        return (
          <div style={detailStyle}>
            <strong>{duplicateResults.inCooldownCount}</strong> contacts are in cooldown period.
          </div>
        );

      case 'spam':
        if (!spamScore) return null;
        return (
          <div style={{
            ...detailStyle,
            background: spamScore.score > 40 ? COLORS.roseBg : COLORS.amberBg,
          }}>
            Spam Score: <strong>{spamScore.score}/100</strong> (Grade: <strong>{spamScore.grade || 'N/A'}</strong>)
            {spamScore.recommendations?.[0] && (
              <div style={{ marginTop: 4, color: COLORS.textSecondary }}>
                {typeof spamScore.recommendations[0] === 'string'
                  ? spamScore.recommendations[0]
                  : spamScore.recommendations[0]?.text || ''}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Overall Progress Header */}
      <div style={{
        ...glassCard,
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
            Pre-Launch Verification
          </div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{recipients.length} contacts</span>
            <span style={{ color: COLORS.border }}>|</span>
            <span>{completedCount}/{STEPS.length} checks complete</span>
            {totalIssues > 0 && (
              <Tag style={{
                color: COLORS.amber, background: COLORS.amberBg, border: 'none',
                borderRadius: 5, fontSize: 10, fontWeight: 600,
              }}>
                {totalIssues} issues
              </Tag>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Progress
            type="circle"
            percent={overallProgress}
            width={48}
            strokeWidth={8}
            strokeColor={allPassed ? COLORS.emerald : hasErrors ? COLORS.rose : COLORS.violet}
            trailColor="rgba(255,255,255,0.06)"
            format={(pct) => (
              <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{pct}%</span>
            )}
          />
          {!isRunning ? (
            <button
              onClick={runAllChecks}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px', borderRadius: 8,
                background: COLORS.violet, border: 'none',
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.target.style.opacity = '0.85'; }}
              onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
            >
              <PlayCircleOutlined />
              {stepStatuses.some((s) => s !== 'pending') ? 'Re-run All' : 'Run All Checks'}
            </button>
          ) : (
            <button
              disabled
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px', borderRadius: 8,
                background: COLORS.surfaceLight, border: `1px solid ${COLORS.border}`,
                color: COLORS.textSecondary, fontSize: 13, fontWeight: 600,
                cursor: 'not-allowed',
              }}
            >
              <LoadingOutlined spin />
              Running...
            </button>
          )}
        </div>
      </div>

      {/* ZeroBounce Toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 4px',
      }}>
        <Switch
          size="small"
          checked={useZeroBounce}
          onChange={setUseZeroBounce}
          style={{ background: useZeroBounce ? COLORS.violet : undefined }}
        />
        <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
          Use ZeroBounce API for deep email verification (consumes API credits)
        </span>
      </div>

      {/* Step Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {STEPS.map((step, i) => {
          const status = stepStatuses[i];
          const statusCfg = STATUS_DISPLAY[status];
          const isActive = activeStep === i && isRunning;
          const issueDetail = getStepIssueDetail(step.key, status);

          return (
            <div
              key={step.key}
              style={{
                ...glassCard,
                padding: 0,
                overflow: 'hidden',
                border: isActive
                  ? `2px solid ${COLORS.violet}`
                  : `1px solid ${COLORS.glassBorder}`,
                transition: 'all 0.2s',
              }}
            >
              {/* Step Header */}
              <div
                onClick={() => {
                  setExpandedSteps((prev) =>
                    prev.includes(step.key)
                      ? prev.filter((k) => k !== step.key)
                      : [...prev, step.key]
                  );
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.surfaceLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Step Icon */}
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `${step.color}18`,
                  color: step.color,
                  fontSize: 14,
                }}>
                  {step.icon}
                </div>

                {/* Step Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.text }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {step.desc}
                  </div>
                </div>

                {/* Status Badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 10px',
                  borderRadius: 6,
                  background: statusCfg.bg,
                  color: statusCfg.color,
                  fontSize: 11,
                  fontWeight: 600,
                }}>
                  {statusCfg.icon}
                  {statusCfg.label}
                </div>

                {/* Expand arrow */}
                {issueDetail && (
                  <CaretRightOutlined
                    style={{
                      color: COLORS.textMuted,
                      fontSize: 10,
                      transform: expandedSteps.includes(step.key) ? 'rotate(90deg)' : 'rotate(0)',
                      transition: 'transform 0.2s',
                    }}
                  />
                )}
              </div>

              {/* Expanded Issue Details */}
              {issueDetail && expandedSteps.includes(step.key) && (
                <div style={{ padding: '0 16px 14px' }}>
                  {issueDetail}
                </div>
              )}

              {/* Running progress bar — real-time for Email Verification, indeterminate for others */}
              {status === 'running' && (
                <div style={{ padding: '0 16px 8px' }}>
                  {(step.key === 'verification' || step.key === 'invalid') && verifyProgress && verifyProgress.total > 0 ? (
                    <>
                      <Progress
                        percent={Math.round((verifyProgress.processed / verifyProgress.total) * 100)}
                        status="active"
                        strokeColor={COLORS.violet}
                        trailColor="rgba(255,255,255,0.04)"
                        size="small"
                        format={(pct) => <span style={{ fontSize: 11, color: COLORS.text }}>{pct}%</span>}
                      />
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 12, marginTop: 4,
                        fontSize: 11, color: COLORS.textSecondary,
                      }}>
                        <span>{verifyProgress.processed}/{verifyProgress.total} checked</span>
                        <span style={{ color: COLORS.emerald }}>
                          {verifyProgress.valid} valid
                        </span>
                        {verifyProgress.invalid > 0 && (
                          <span style={{ color: COLORS.rose }}>
                            {verifyProgress.invalid} invalid
                          </span>
                        )}
                        {verifyProgress.cached > 0 && (
                          <span style={{ color: COLORS.blue }}>
                            {verifyProgress.cached} cached
                          </span>
                        )}
                      </div>
                      {verifyProgress.message && (
                        <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>
                          {verifyProgress.message}
                        </div>
                      )}
                    </>
                  ) : (
                    <Progress
                      percent={99}
                      status="active"
                      showInfo={false}
                      strokeColor={COLORS.violet}
                      trailColor="rgba(255,255,255,0.04)"
                      size="small"
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action Bar */}
      {(allPassed || hasWarnings) && !isRunning && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 4,
        }}>
          {hasWarnings && (
            <button
              onClick={handleAutoClean}
              disabled={isCleaning}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                background: COLORS.glass, border: `1px solid ${COLORS.glassBorder}`,
                color: isCleaning ? COLORS.textMuted : COLORS.textSecondary,
                fontSize: 12, fontWeight: 600,
                cursor: isCleaning ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { if (!isCleaning) { e.target.style.borderColor = COLORS.amber; e.target.style.color = COLORS.amber; } }}
              onMouseLeave={(e) => { e.target.style.borderColor = COLORS.glassBorder; e.target.style.color = COLORS.textSecondary; }}
            >
              {isCleaning ? <LoadingOutlined spin /> : <ClearOutlined />}
              {isCleaning ? 'Cleaning...' : `Auto-Clean All (${totalIssues} issues)`}
            </button>
          )}

          <button
            onClick={() => {
              message.info('Continuing verification in background...');
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8,
              background: COLORS.glass, border: `1px solid ${COLORS.glassBorder}`,
              color: COLORS.textSecondary, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.target.style.borderColor = COLORS.blue; e.target.style.color = COLORS.blue; }}
            onMouseLeave={(e) => { e.target.style.borderColor = COLORS.glassBorder; e.target.style.color = COLORS.textSecondary; }}
          >
            <ThunderboltOutlined />
            Continue in Background
          </button>

          <button
            onClick={() => {
              if (onComplete) onComplete({ fallbackValues, passed: allPassed });
            }}
            disabled={hasErrors}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 20px', borderRadius: 8,
              background: allPassed ? COLORS.emerald : COLORS.amber,
              border: 'none',
              color: '#fff', fontSize: 13, fontWeight: 600,
              cursor: hasErrors ? 'not-allowed' : 'pointer',
              opacity: hasErrors ? 0.5 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { if (!hasErrors) e.target.style.opacity = '0.85'; }}
            onMouseLeave={(e) => { e.target.style.opacity = hasErrors ? '0.5' : '1'; }}
          >
            <CheckCircleOutlined />
            {allPassed ? 'All Checks Passed -- Continue' : 'Continue with Warnings'}
          </button>
        </div>
      )}

      {/* Removed Contacts Summary — shows after auto-clean */}
      {cleanedContacts.length > 0 && (
        <div style={{
          ...glassCard,
          padding: 16,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
              Removed Contacts ({cleanedContacts.length})
            </div>
            <button
              onClick={() => setCleanedContacts([])}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11,
                background: COLORS.glass, border: `1px solid ${COLORS.glassBorder}`,
                color: COLORS.textSecondary, cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
          <div style={{
            maxHeight: 240, overflowY: 'auto',
            borderRadius: 8, border: `1px solid ${COLORS.glassBorder}`,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: COLORS.surfaceLight }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textSecondary, fontWeight: 600 }}>Email</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textSecondary, fontWeight: 600 }}>Contact</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textSecondary, fontWeight: 600 }}>Company</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textSecondary, fontWeight: 600 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {cleanedContacts.map((c, idx) => (
                  <tr key={idx} style={{ borderTop: `1px solid ${COLORS.glassBorder}` }}>
                    <td style={{ padding: '6px 12px', color: COLORS.rose }}>{c.email}</td>
                    <td style={{ padding: '6px 12px', color: COLORS.text }}>{c.contactName || '—'}</td>
                    <td style={{ padding: '6px 12px', color: COLORS.textSecondary }}>{c.agencyName || '—'}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <Tag style={{
                        color: COLORS.rose, background: COLORS.roseBg,
                        border: 'none', borderRadius: 4, fontSize: 10,
                      }}>
                        {c.reason || c.code || 'Invalid'}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationPanel;
